/** FIRM — the desk's firm, its people, and the clients who have agreed to share. */
import { useMemo } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useDispatch, useSelector } from 'react-redux';
import { FIRM_CLIENTS, FIRM_MEMBERS, MY_FIRM_MEMBERSHIP, SEARCH_FIRMS } from '../queries';
import { REFRESH_TOKEN } from '../mutations/auth';
import { loginSuccess } from '../../state/authSlice';
import { setAssignedRole } from '../../state/uiSlice';
import { resolveSessionRole } from '../session';
import { INDIVIDUAL_SEAT } from '../../config/navigation';
import { EMPTY } from './_shared';

/**
 * The signed-in user's firm membership, in any status. `approved` is the only
 * state that grants firm access; `pending` is a request the owner has not
 * answered; `membership` is null for someone with no firm yet.
 *
 * `skip` is for a screen that only needs the answer once something opens — a
 * closed modal should not keep asking.
 */
export function useMyFirm({ skip = false } = {}) {
  const { data, loading, error, refetch } = useQuery(MY_FIRM_MEMBERSHIP, {
    skip,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });
  const membership = data?.myFirmMembership ?? null;
  return {
    loading,
    error,
    refetch,
    membership,
    /**
     * Whether the question has been answered yet — cache or network.
     *
     * `loading` is not that: under `cache-and-network` it goes true again on
     * every remount even with the answer already in hand, and a screen that
     * gates on it blanks out on each visit. Read this where "do we know?"
     * is the question, and `loading` only for a first, cold read.
     */
    ready: !loading || Boolean(data),
    approved: membership?.membershipStatus === 'APPROVED',
    pending: membership?.membershipStatus === 'PENDING',
    rejected: membership?.membershipStatus === 'REJECTED',
  };
}

/** A firm's people: the approved roster, or (owner only) `status: 'PENDING'`. */
export function useFirmMembers(organizationId, { status = null, skip = false } = {}) {
  const { data, loading, error, refetch } = useQuery(FIRM_MEMBERS, {
    variables: { organizationId, status },
    skip: skip || !organizationId,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });
  return { loading, error, refetch, members: data?.firmMembers ?? EMPTY };
}

/** Firms whose name contains `name`; nothing is asked below two characters. */
export function useFirmSearch(name) {
  const term = (name || '').trim();
  const { data, loading, error } = useQuery(SEARCH_FIRMS, {
    variables: { name: term },
    skip: term.length < 2,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });
  return { loading, error, firms: term.length < 2 ? EMPTY : data?.searchFirms ?? EMPTY };
}

/** The caller's firm's client links. Only APPROVED ones share data with the firm. */
export function useFirmClients({ skip = false } = {}) {
  const { data, loading, error, refetch } = useQuery(FIRM_CLIENTS, {
    skip,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });
  return {
    loading,
    error,
    refetch,
    links: data?.firmClients ?? EMPTY,
    /* See `useMyFirm.ready`. A skipped query has answered as much as it ever
       will, so it counts as ready. */
    ready: skip || !loading || Boolean(data),
  };
}

/**
 * THE client scope: the ids of the users whose data this desk may show.
 *
 * A client reaches a firm's book by consent — `firmClients` holds one link per
 * person asked, and only an APPROVED one shares anything. So every screen about
 * people (the book roll-up, the client table, the deep dive, KYC, proposals)
 * reads its subjects through this, and a fresh desk with no approved clients
 * shows an empty book rather than the platform's other users.
 *
 * `ids` are strings, because that is what a portfolio's `userId` is; `set` is
 * the same thing for membership tests.
 *
 * A session with no approved firm is NOT a failure here. `firmClients` is
 * firm-gated on the server, so asking it without a membership answers
 * "403: approved firm membership required" and nothing else — and carrying that
 * as an error put a raw 403 on half the screens and, worse, made the dashboard
 * treat a brand-new account as a broken one and hide the way in. So the query
 * is not sent at all until the membership is APPROVED, and `noFirm` says why
 * the scope is empty, for screens that should offer Firm & team instead of
 * "no clients yet".
 */
export function useClientScope() {
  /* An individual IS the scope: no firm, no links — their own user id, which
     every per-client read then asks for (the backend answers a caller for
     themselves whatever the target header says). */
  const role = useSelector((s) => s.ui.role);
  const selfId = useSelector((s) => s.auth.userId);
  const self = role === INDIVIDUAL_SEAT;
  const firm = useMyFirm({ skip: self });
  const clients = useFirmClients({ skip: self || !firm.approved });
  const { links, error, refetch, ready } = clients;
  return useMemo(() => {
    if (self) {
      const ids = selfId ? [String(selfId)] : EMPTY;
      return {
        loading: false, error: null, noFirm: false, membership: null, self: true, refetch: () => Promise.resolve(null), ids, set: new Set(ids),
      };
    }
    const ids = firm.approved
      ? links.filter((l) => l.status === 'APPROVED' && l.clientUserId != null).map((l) => String(l.clientUserId))
      : EMPTY;
    return {
      self: false,
      /* Cold reads only — see `useMyFirm.ready`. */
      loading: !firm.ready || (firm.approved && !ready),
      /* The firm gate is not an error; a real failure of either read is. */
      error: firm.error || (firm.approved ? error : null),
      noFirm: firm.ready && !firm.approved,
      membership: firm.membership,
      refetch,
      ids,
      set: new Set(ids),
    };
  }, [self, selfId, firm.approved, firm.ready, firm.error, firm.membership, links, error, refetch, ready]);
}

/**
 * Swap in a freshly minted access token, so a firm role granted since sign-in
 * (a firm just created, or a request just approved) becomes the seat without
 * signing out. Resolves to the assigned seat, or null when there is none yet.
 */
export function useRefreshSession() {
  const dispatch = useDispatch();
  const auth = useSelector((s) => s.auth);
  const [refresh] = useMutation(REFRESH_TOKEN);
  return async () => {
    const stored = auth.refreshToken || localStorage.getItem('refreshToken');
    if (!stored) return null;
    const { data } = await refresh({ variables: { refreshToken: stored } });
    const next = data?.refreshToken;
    if (!next?.success || !next.authToken) return null;
    dispatch(loginSuccess({
      authToken: next.authToken,
      refreshToken: next.refreshToken || stored,
      userId: auth.userId,
      emailVerified: next.emailVerified,
    }));
    const { assigned } = resolveSessionRole({ token: next.authToken, userId: auth.userId });
    dispatch(setAssignedRole(assigned));
    return assigned;
  };
}

/**
 * The organisation this session's user works at, and its people — the firm
 * they are an APPROVED member of. `memberIds` is colleagues only (numeric user
 * ids); anyone without an approved firm has none.
 */
export function useOrganization() {
  const firm = useMyFirm();
  const m = firm.approved ? firm.membership : null;
  const roster = useFirmMembers(m?.organizationId, { skip: !m });
  return useMemo(() => ({
    loading: firm.loading || roster.loading,
    userId: m?.userId != null ? String(m.userId) : null,
    org: m ? { organizationId: m.organizationId, name: m.organizationName, organizationType: m.organizationType } : null,
    name: m?.organizationName ?? null,
    memberIds: roster.members
      .map((r) => (r.userId == null ? null : String(r.userId)))
      .filter((v) => v && v !== String(m?.userId)),
  }), [firm.loading, roster.loading, roster.members, m]);
}
